import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Button,
  Tooltip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { AdminBoardItemDto } from "@/dto/AdminBoardDto";
import { Board as BoardOrganism } from "@/organism";
import useBoardList from "./useBoardList";

// Board 컴포넌트 props 타입 정의
interface BoardProps {
  isLoading: boolean;
  boardList: AdminBoardItemDto[];
  page: number;
  limit: number;
  totalCount: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  comId: string;
  currentAdminId: string;
}

export const Board = ({
  isLoading,
  boardList,
  page,
  limit,
  totalCount,
  onPageChange,
  comId,
  currentAdminId,
}: BoardProps) => {
  const theme = useTheme();

  // useBoardList 훅을 사용하여 상태와 핸들러 가져오기
  const {
    searchTerm,
    selectedCategory,
    filteredList,
    selectedBoardItem,
    isDetailModalOpen,
    isDetailLoading,
    isCreateModalOpen,
    pageCategories,
    sectionCategories,
    handleSearchChange,
    handleCategoryClick,
    handleBoardItemClick,
    handleCloseDetailModal,
    handleOpenCreateModal,
    handleCloseCreateModal,
    setSelectedCategory,
  } = useBoardList({ boardList });

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "yyyy.MM.dd", { locale: ko });
    } catch (error) {
      return "-";
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* 헤더 및 검색 영역 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          자료 요청 게시판
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="제목 또는 작성자 검색"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={handleOpenCreateModal}
          >
            새 자료요청 작성
          </Button>
        </Box>
      </Box>

      {/* 페이지 필터 영역 */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        {/* 전체 필터 옵션 - 별도 위치에 배치 */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <Chip
            label="전체 보기"
            clickable
            color={selectedCategory === null ? "primary" : "default"}
            onClick={() => setSelectedCategory(null)}
            sx={{
              fontWeight: selectedCategory === null ? 600 : 400,
              backgroundColor:
                selectedCategory === null
                  ? alpha(theme.palette.success.main, 0.1)
                  : undefined,
              borderColor:
                selectedCategory === null
                  ? alpha(theme.palette.success.main, 0.3)
                  : undefined,
              color:
                selectedCategory === null
                  ? theme.palette.success.main
                  : undefined,
              borderWidth: 1,
              borderStyle: "solid",
            }}
          />
        </Box>

        <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
          페이지
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {pageCategories.map((category) => (
            <Chip
              key={category}
              label={category}
              clickable
              color={selectedCategory === category ? "primary" : "default"}
              onClick={() => handleCategoryClick(category)}
              sx={{ fontWeight: selectedCategory === category ? 600 : 400 }}
            />
          ))}
        </Box>

        <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
          섹션
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {sectionCategories.map((category) => (
            <Chip
              key={category}
              label={category}
              clickable
              color={selectedCategory === category ? "primary" : "default"}
              onClick={() => handleCategoryClick(category)}
              sx={{ fontWeight: selectedCategory === category ? 600 : 400 }}
            />
          ))}
        </Box>
      </Box>

      {/* 게시판 테이블 */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <TableCell width="8%" align="center">
                번호
              </TableCell>
              <TableCell width="15%" align="center">
                페이지
              </TableCell>
              <TableCell width="15%" align="center">
                섹션
              </TableCell>
              <TableCell>제목</TableCell>
              <TableCell width="12%" align="center">
                작성자
              </TableCell>
              <TableCell width="12%" align="center">
                등록일
              </TableCell>
              <TableCell width="8%" align="center">
                조회
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // 로딩 중일 때 표시
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : filteredList.length > 0 ? (
              // 데이터가 있을 때 표시
              filteredList.map((item) => (
                <TableRow
                  key={item.categoryId + (item.boardId || "empty")}
                  hover
                  onClick={
                    item.hasPosts
                      ? () => handleBoardItemClick(item.boardId)
                      : undefined
                  }
                  sx={{
                    cursor: item.hasPosts ? "pointer" : "default",
                    backgroundColor: !item.hasPosts
                      ? alpha(theme.palette.grey[100], 0.5)
                      : undefined,
                  }}
                >
                  <TableCell align="center">{item.boardId || "-"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.pageCategoryName}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.sectionCategoryName}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {item.hasPosts ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">
                          {item.boardTitle}
                        </Typography>

                        {/* 첨부파일, 댓글 아이콘 */}
                        {Number(item.fileCount) > 0 && (
                          <Tooltip title={`첨부파일 ${item.fileCount}개`}>
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "text.secondary",
                              }}
                            >
                              <AttachFileIcon fontSize="small" />
                              {item.fileCount}
                            </Box>
                          </Tooltip>
                        )}

                        {Number(item.commentCount) > 0 && (
                          <Tooltip title={`댓글 ${item.commentCount}개`}>
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "text.secondary",
                              }}
                            >
                              <CommentIcon fontSize="small" />
                              {item.commentCount}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          등록된 자료가 없습니다
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">{item.admName || "-"}</TableCell>
                  <TableCell align="center">
                    {formatDate(item.regDttm)}
                  </TableCell>
                  <TableCell align="center">
                    {item.viewCount ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <VisibilityIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", fontSize: 16 }}
                        />
                        {item.viewCount}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              // 데이터가 없을 때 표시
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  게시물이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(totalCount / limit)}
          page={page}
          onChange={onPageChange}
          color="primary"
          showFirstButton
          showLastButton
          disabled={isLoading}
        />
      </Box>

      {/* 게시판 상세 조회 모달 */}
      {selectedBoardItem && (
        <BoardOrganism.Detail
          open={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          boardItem={selectedBoardItem}
          isLoading={isDetailLoading}
          admIdntfcId={currentAdminId}
        />
      )}

      {/* 게시판 새 자료 요청 모달 */}
      <BoardOrganism.Create
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        comId={comId}
        onSuccess={() => {}}
      />
    </Box>
  );
};
